'use client';

import Link from 'next/link';
import React, { useState } from 'react';
import { sectionHeadingTag } from '@/components/dynamic-zone/section-heading-level';
import { cn } from '../../../lib/utils';

interface BulletPoint {
	id: number;
	text: string;
}

export interface Button {
	text: string;
	URL: string;
	target?: '_blank' | '_self' | '_parent' | '_top';
	variant?:
		| 'simple'
		| 'outline'
		| 'primary'
		| 'muted'
		| 'default'
		| 'destructive'
		| 'secondary'
		| 'ghost'
		| 'link';
	popup?: {
		title?: string;
		content?: string;
		trigger?: string;
	} | null;
}

export interface JobPosition {
	id: number;
	title: string;
	location: string;
	department: string;
	experience: string;
	preferred_skills?: string;
	responsibilities: BulletPoint[];
	qualifications: BulletPoint[];
	CTA: Button;
}

export interface JobOpeningsProps {
	id: number;
	heading: string;
	job_positions: JobPosition[];
	background_color?: string;
	__component: string;
}

interface JobDetailsContentProps {
	job: JobPosition;
}

const JobDetailsContent = ({ job }: JobDetailsContentProps) => (
	<div className="job-details-panel p-4">
		{job.preferred_skills && (
			<div className="mb-4">
				<h4 className="font-semibold text-gray-700">
					Preferred Skills:
				</h4>
				<p className="text-gray-600">{job.preferred_skills}</p>
			</div>
		)}

		<div className="mb-4">
			<h4 className="font-semibold text-gray-700">
				Key Responsibilities:
			</h4>
			<ul className="ml-5 mt-2 list-disc space-y-1 text-gray-600">
				{job.responsibilities.map(point => (
					<li key={point.id}>{point.text}</li>
				))}
			</ul>
		</div>

		<div>
			<h4 className="font-semibold text-gray-700">
				Qualifications and Skills:
			</h4>
			<ul className="ml-5 mt-2 list-disc space-y-1 text-gray-600">
				{job.qualifications.map(point => (
					<li key={point.id}>{point.text}</li>
				))}
			</ul>
		</div>
	</div>
);

export const JobOpenings = ({
	heading = 'Current Job Openings',
	job_positions,
	background_color = 'bg-white',
	isFirst = false,
}: JobOpeningsProps & { isFirst?: boolean }) => {
	const SectionHeading = sectionHeadingTag(isFirst);
	const [expandedJob, setExpandedJob] = useState<number | null>(null);

	if (!job_positions || job_positions.length === 0) {
		console.warn('No job positions provided');
		return null;
	}

	const toggleJobDetails = (id: number) => {
		setExpandedJob(expandedJob === id ? null : id);
	};

	return (
		<section
			className={cn(
				'job-openings-section w-full py-16',
				background_color,
			)}>
			<noscript>
				<div className="mx-auto max-w-4xl">
					{job_positions.map(job => (
						<article key={`noscript-${job.id}`} className="mb-6">
							<h3>{job.title}</h3>
							<p>Location: {job.location}</p>
							<p>Department: {job.department}</p>
							<p>Experience: {job.experience}</p>
							<JobDetailsContent job={job} />
						</article>
					))}
				</div>
			</noscript>
			<div className="container mx-auto px-4 md:px-6">
				{/* Heading */}
				<SectionHeading className="mb-8 text-center text-3xl font-bold text-gray-900">
					{heading}
				</SectionHeading>

				{/* Job Listings */}
				<div className="mx-auto max-w-4xl">
					{job_positions.map(job => (
						<div
							key={job.id}
							className="mb-6 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
							{/* Job Header - Always visible */}
							<div
								className="flex cursor-pointer flex-col border-b border-gray-100 p-4 hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between"
								onClick={() => toggleJobDetails(job.id)}>
								<div className="mb-2 sm:mb-0">
									<h3 className="font-bold text-blue-600">
										{job.title}
									</h3>
									<div className="mt-1 flex flex-wrap text-sm text-gray-600">
										<span className="mr-4">
											Location: {job.location}
										</span>
										<span className="mr-4">
											Department: {job.department}
										</span>
										<span>
											Experience: {job.experience}
										</span>
									</div>
								</div>
								<Link
									href={job.CTA.URL}
									target={job.CTA.target || '_self'}
									className={cn(
										'mt-3 inline-flex rounded-md px-4 py-2 text-sm font-medium sm:mt-0',
										{
											'bg-blue-600 text-white hover:bg-blue-700':
												!job.CTA.variant ||
												job.CTA.variant === 'primary' ||
												job.CTA.variant === 'default',
											'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50':
												job.CTA.variant === 'outline',
											'text-gray-700 hover:text-gray-900':
												job.CTA.variant === 'ghost',
											'bg-red-600 text-white hover:bg-red-700':
												job.CTA.variant ===
												'destructive',
											'bg-gray-600 text-white hover:bg-gray-700':
												job.CTA.variant === 'secondary',
											'text-blue-600 hover:text-blue-800':
												job.CTA.variant === 'link',
											'bg-gray-100 text-gray-900 hover:bg-gray-200':
												job.CTA.variant === 'muted',
											'bg-white text-gray-900 hover:bg-gray-100':
												job.CTA.variant === 'simple',
										},
									)}
									onClick={e => e.stopPropagation()}>
									{job.CTA.text || 'Apply Now'}
								</Link>
							</div>

							{expandedJob === job.id && (
								<JobDetailsContent job={job} />
							)}
						</div>
					))}
				</div>
			</div>
		</section>
	);
};

export default JobOpenings;
